import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { FieldConfig, RadioComponent } from 'hig-ng-lib';
import { Subscription, firstValueFrom } from 'rxjs';
import { AppConfig } from '../../../../../config/app-config';
import {
    CLAIMANT_DRIVER_NAME_ERROR,
    CLAIMANT_NAME_PATTERN_ERROR,
    CLAIM_INPUT_ERROR,
    CLAIM_PATTERN_ERROR,
    CLAIM_SELECT_ERROR
} from '../../../../../treo.properties';
import { ListClaimDetails } from '../../../../views/claims/claims.model';
import { ClaimsService } from '../../../../views/claims/claims.service';
import { ApiService } from '../../../services/api.service';
import { LoaderService } from '../../../services/loading.service';
import { MenuService } from '../../../services/menu.service';
import { SharedService } from '../../../services/shared.service';

interface MessageFieldConfigType {
    name: string;
    showField: boolean;
}
interface ErrorMsgType {
    messageText: string;
    messageType: string;
}
@Component({
    selector: 'app-claim-menu',
    templateUrl: './claim-menu.component.html',
    standalone: false
})
export class ClaimMenuComponent implements OnInit, AfterViewInit, OnDestroy {
    public JSON = JSON;
    private static readonly NO_MATCH_ERROR = 'No Claim Found';
    private static readonly CLAIM_LIST_PAGE_SIZE = 500;
    claimForm: FormGroup;
    inputString: string;
    selectedClaimRadio: string;
    claimSearchSubscription$: Subscription;
    currentNumberSubscription$: Subscription;
    orgnztnId: string;
    currentHeaderTabSubscription$: Subscription;
    currentHeaderTab: string = '';
    headerTabChanged: boolean = false;
    private policyNumbersForEvaluation: string[] = [];
    private claimListValidationCacheComplete: boolean = false;
    private policyNumbersCacheAccountKey: string = '';
    @ViewChild('claimMenuRadioTemplate') claimMenuRadioTemplate: RadioComponent;

    searchField: FieldConfig = {
        type: 'input',
        showField: true,
        name: 'searchField',
        subType: 'text',
        validations: [
            {
                name: 'required',
                message: CLAIM_INPUT_ERROR
            }
        ]
    };

    constructor(
        private fb: UntypedFormBuilder,
        private loaderService: LoaderService,
        private claimsService: ClaimsService,
        private apiService: ApiService,
        private SharedService: SharedService,
        private router: Router,
        private menuService: MenuService,
        private cdr: ChangeDetectorRef
    ) {}
    claimMenuRadioConfig = AppConfig.claimMenuRadioConfig;
    claimMenuButtonClearConfig = AppConfig.claimMenuButtonClearConfig;
    claimMenuButtonSearchConfig = AppConfig.claimMenuButtonSearchConfig;
    messageFieldConfig: MessageFieldConfigType = {
        name: 'searchErrorMsg',
        showField: true
    };
    errorMsg: ErrorMsgType = {
        messageText: '',
        messageType: 'error'
    };

    private normalizeClaimNumber(value: string): string {
        return (value || '').replace(/\s+/g, '').toUpperCase();
    }

    private getClaimsListValuationDate(): string {
        const rawValuationData = this.loaderService.getItem('valuationDate') as any;
        let valuationData = rawValuationData;

        // Some flows store valuation date as a JSON string; support both shapes.
        if (typeof rawValuationData === 'string') {
            try {
                valuationData = JSON.parse(rawValuationData);
            } catch {
                valuationData = null;
            }
        }

        const valuationDate = valuationData?.lastDefaultValuationDate;
        if (!valuationDate) {
            return '';
        }

        return `${valuationDate.slice(0, 10)} 00:00:00`;
    }

    private getCachedPolicyNumbers(): string[] {
        const cacheData = this.SharedService?.getClaimListCacheData ? this.SharedService.getClaimListCacheData() : null;
        return cacheData?.claimNumbers || [];
    }

    private getCurrentAccountCacheKey(): string {
        const orgId = this.loaderService.getItem('orgId');
        const appAccountDetailId = this.loaderService.getItem('appAccountDetailId');
        const valuationDate = this.getClaimsListValuationDate();
        return orgId !== null && orgId !== undefined
            ? `${String(orgId)}:${String(appAccountDetailId ?? '')}:${valuationDate}`
            : '';
    }

    private resetLocalPolicyCacheIfAccountChanged(): void {
        const currentAccountKey = this.getCurrentAccountCacheKey();
        if (this.policyNumbersCacheAccountKey && this.policyNumbersCacheAccountKey !== currentAccountKey) {
            this.policyNumbersForEvaluation = [];
            this.claimListValidationCacheComplete = false;
        }
        this.policyNumbersCacheAccountKey = currentAccountKey;
    }

    private async loadPolicyNumbersForEvaluation(): Promise<boolean> {
        const valuationDate = this.getClaimsListValuationDate();
        if (!valuationDate) {
            this.errorMsg.messageText = ClaimMenuComponent.NO_MATCH_ERROR;
            return false;
        }

        const requestAccountKey = this.getCurrentAccountCacheKey();

        // If cache already contains all rows, reuse it and avoid pagination calls.
        const existingCache = this.SharedService?.getClaimListCacheData ? this.SharedService.getClaimListCacheData() : null;
        const cachedClaimNumbers = existingCache?.claimNumbers || [];
        const cachedTotal = Number(existingCache?.claimListRecords || 0);
        if (cachedTotal > 0 && cachedClaimNumbers.length >= cachedTotal) {
            this.policyNumbersForEvaluation = cachedClaimNumbers;
            this.claimListValidationCacheComplete = true;
            this.policyNumbersCacheAccountKey = this.getCurrentAccountCacheKey();
            return true;
        }

        const collectedClaimNumbers = new Set<string>();
        let pageNumber = 0;
        let totalRecords = 0;
        let shouldContinue = true;

        try {
            while (shouldContinue) {
                if (this.getCurrentAccountCacheKey() !== requestAccountKey) {
                    return false;
                }

                const payload = {
                    claimInfo: {
                        shortInfo: {
                            valuationDate
                        },
                        applicationAccntDtlId: this.getApplicationId() ?? 0,
                        applctnUsrID: this.loaderService.getItem('applctnUserId'),
                        orgnztnId: this.loaderService.getItem('orgId')
                    },
                    userId: this.loaderService.getItem('userId'),
                    claimDetailType: 'CLAIM_LIST',
                    actionType: 'RETRIEVE',
                    claimAllFiltersCriteria: {
                        claimFilterCriteria: {
                            filterId: null
                        }
                    }
                };

                const resp: any = await firstValueFrom(this.claimsService.searchClaimDetailByClaimNumber(payload));
                if (this.getCurrentAccountCacheKey() !== requestAccountKey) {
                    return false;
                }

                this.SharedService.cacheClaimListData(resp);

                const datasource = resp?.listClaimDetails?.[0]?.claims?.[0]?.refineResults?.datasource || [];
                datasource
                    .map((item: any) => item?.claimNumber)
                    .filter((claimNumber: string | null | undefined) => !!claimNumber)
                    .forEach((claimNumber: string) => collectedClaimNumbers.add(claimNumber.toString().trim()));

                if (pageNumber === 0) {
                    totalRecords = Number(
                        resp?.mapTotalNoOfRows?.claimListRecords ??
                            resp?.mapTotalNoOfRows?.['Claim WatchList Size'] ??
                            datasource.length
                    );
                }

                pageNumber += 1;

                if (totalRecords > 0) {
                    shouldContinue = collectedClaimNumbers.size < totalRecords && datasource.length > 0;
                } else {
                    shouldContinue = datasource.length === ClaimMenuComponent.CLAIM_LIST_PAGE_SIZE;
                }
            }

            if (this.getCurrentAccountCacheKey() !== requestAccountKey) {
                return false;
            }

            this.policyNumbersForEvaluation = Array.from(collectedClaimNumbers);
            this.claimListValidationCacheComplete = true;
            this.policyNumbersCacheAccountKey = requestAccountKey;
            return true;
        } catch (error: any) {
            this.errorMsg.messageText = error?.error?.errorMessage || ClaimMenuComponent.NO_MATCH_ERROR;
            return false;
        }
    }

    private async ensurePolicyNumbersForEvaluation(): Promise<boolean> {
        this.resetLocalPolicyCacheIfAccountChanged();

        if (this.claimListValidationCacheComplete && this.policyNumbersForEvaluation.length) {
            return true;
        }

        const cachedPolicyNumbers = this.getCachedPolicyNumbers();
        if (cachedPolicyNumbers.length && this.claimListValidationCacheComplete) {
            this.policyNumbersForEvaluation = cachedPolicyNumbers;
            return true;
        }

        return this.loadPolicyNumbersForEvaluation();
    }

    private isClaimNumberAvailableInCache(claimNumber: string): boolean {
        const cachedClaimNumbers = this.policyNumbersForEvaluation?.length
            ? this.policyNumbersForEvaluation
            : this.getCachedPolicyNumbers();

        if (!cachedClaimNumbers.length) {
            // Preserve legacy behavior only before validation completes.
            return !this.claimListValidationCacheComplete;
        }

        const normalizedInput = this.normalizeClaimNumber(claimNumber);
        return cachedClaimNumbers.some((cachedClaimNumber: string) => {
            return this.normalizeClaimNumber(cachedClaimNumber) === normalizedInput;
        });
    }

    private filterClaimantResultsByValidatedClaims(response: any): any {
        const datasource = response?.listClaimDetails?.[0]?.claims?.[0]?.refineResults?.datasource || [];
        const filteredDatasource = datasource.filter((item: any) => this.isClaimNumberAvailableInCache(item?.claimNumber));

        if (!filteredDatasource.length) {
            return null;
        }

        // If nothing changed, keep original object shape.
        if (filteredDatasource.length === datasource.length) {
            return response;
        }

        const filteredResponse = {
            ...response,
            listClaimDetails: [...(response?.listClaimDetails || [])]
        };

        if (filteredResponse.listClaimDetails.length > 0) {
            const firstDetail = { ...filteredResponse.listClaimDetails[0] };
            const claims = [...(firstDetail.claims || [])];
            if (claims.length > 0) {
                const firstClaim = { ...claims[0] };
                const refineResults = { ...(firstClaim.refineResults || {}) };
                refineResults.datasource = filteredDatasource;
                firstClaim.refineResults = refineResults;
                claims[0] = firstClaim;
            }
            firstDetail.claims = claims;
            filteredResponse.listClaimDetails[0] = firstDetail;
        }

        if (response?.mapTotalNoOfRows) {
            const mapTotalNoOfRows = { ...response.mapTotalNoOfRows };
            let totalUpdated = false;
            Object.keys(mapTotalNoOfRows).forEach((key: string) => {
                if (/name\s*list\s*records/i.test(key)) {
                    mapTotalNoOfRows[key] = filteredDatasource.length;
                    totalUpdated = true;
                }
            });

            if (!totalUpdated && Object.keys(mapTotalNoOfRows).length === 1) {
                const firstKey = Object.keys(mapTotalNoOfRows)[0];
                mapTotalNoOfRows[firstKey] = filteredDatasource.length;
            }

            filteredResponse.mapTotalNoOfRows = mapTotalNoOfRows;
        }

        return filteredResponse;
    }

    private shouldUseClaimListValidation(): boolean {
        // Only validate claim numbers for CAV accounts
        // For CAPS accounts, skip validation - all claims are present
        return this.isCAVAccount();
    }

    ngAfterViewInit(): void {
        this.currentNumberSubscription$ = this.SharedService.currentNumber.subscribe((res: string) => {
            if (res) {
                this.orgnztnId = res;
            }
        });
        this.currentHeaderTabSubscription$ = this.SharedService.currentLink.subscribe((res: any) => {
            this.headerTabChanged = true;
            this.clearData();
        });
        this.claimMenuRadioTemplate.field = this.claimMenuRadioConfig;
    }

    ngOnInit() {
        this.claimForm = this.fb.group({
            claimRadio: [''],
            searchField: ['']
        });

        // Trigger change detection after form initialization
        setTimeout(() => {
            this.cdr.detectChanges();
        });
    }

    get claimRadioValue(): string {
        return this.claimForm?.controls?.['claimRadio']?.value || '';
    }

    checkStringWithRegex(inputString: string): boolean {
        const regex = /^\s*[a-zA-Z0-9]{3}\s*[a-zA-Z]{1,2}\s*\d{5}\s*$/;
        return regex.test(inputString);
    }

    checkNoSpecialCharacters(input: string): boolean {
        const noSpecialCharRegex = /^[a-zA-Z0-9 ]*$/;
        return noSpecialCharRegex.test(input);
    }

    async search() {
        this.errorMsg.messageText = '';
        if (
            this.claimForm.controls['searchField'].value !== '' &&
            this.claimForm.controls['searchField'].value !== null
        ) {
            if (this.claimForm.controls['claimRadio'].value === 'claimNumber') {
                if (this.checkStringWithRegex(this.claimForm.controls['searchField'].value)) {
                    if (this.shouldUseClaimListValidation()) {
                        const policyNumbersLoaded = await this.ensurePolicyNumbersForEvaluation();
                        if (!policyNumbersLoaded) {
                            return;
                        }
                    }
                    this.fetchClaimDetails();
                } else {
                    this.errorMsg.messageText = CLAIM_PATTERN_ERROR;
                }
            } else {
                if (this.checkNoSpecialCharacters(this.claimForm.controls['searchField'].value)) {
                    if (this.shouldUseClaimListValidation()) {
                        const policyNumbersLoaded = await this.ensurePolicyNumbersForEvaluation();
                        if (!policyNumbersLoaded) {
                            return;
                        }
                    }
                    this.fetchClaimantDetails();
                    this.menuService.menuHeading.next(AppConfig.claimantPageHeadingConfig);
                } else {
                    this.errorMsg.messageText = CLAIMANT_NAME_PATTERN_ERROR;
                }
            }
        } else {
            this.setErrorMsg();
        }
        this.claimForm.controls['claimRadio'].valueChanges.subscribe((selectedValue) => {
            if (selectedValue === 'claimNumber' || selectedValue === 'claimantName') this.errorMsg.messageText = '';
        });
    }

    private setErrorMsg() {
        if (this.claimForm.controls['claimRadio'].value === 'claimNumber')
            this.errorMsg.messageText = CLAIM_INPUT_ERROR;
        else if (this.claimForm.controls['claimRadio'].value === 'claimantName')
            this.errorMsg.messageText = CLAIMANT_DRIVER_NAME_ERROR;
        else this.errorMsg.messageText = CLAIM_SELECT_ERROR;
    }
    clearData() {
        let selectedRadio = this.claimForm.controls['claimRadio'].value;
        this.errorMsg.messageText = '';
        this.claimForm.reset();
        this.claimForm.clearValidators();
        if (!this.headerTabChanged) this.claimForm.controls['claimRadio'].setValue(selectedRadio);
        this.headerTabChanged = false;
    }

    getApplicationId() {
        const encodedId = this.loaderService.getItem('appAccountDetailId');
        if (!encodedId) return null;

        const decodedId = encodedId;
        return Number(decodedId) === 0 ? null : decodedId;
    }

    private isCAVAccount(): boolean {
        const accountName = sessionStorage.getItem('accountName') || '';
        // CAV accounts have mixed case, CAPS accounts are all uppercase
        return accountName !== accountName.toUpperCase();
    }

    fetchClaimDetails() {
        if (this.shouldUseClaimListValidation()) {
            const claimNumber = this.claimForm.controls['searchField'].value;
            if (!this.isClaimNumberAvailableInCache(claimNumber)) {
                this.errorMsg.messageText = ClaimMenuComponent.NO_MATCH_ERROR;
                return;
            }
        }

        let userId = this.loaderService.getItem('userId');
        let payload = {
            claimInfo: {
                claimNumber: this.claimForm.controls['searchField'].value,
                orgnztnId: this.loaderService.getItem('orgId'),
                applicationAccntDtlId: this.getApplicationId()
            },
            userId: userId,
            claimDetailType: 'CLAIM_MENU_BASIC_SEARCH',
            claimType: 'HARTFORD',
            actionType: 'RETRIEVE'
        };
        this.claimSearchSubscription$ = this.claimsService.searchClaimDetailByClaimNumber(payload).subscribe({
            next: (resp: ListClaimDetails) => {
                this.SharedService.setclaimDetails(resp.listClaimDetails[0].claims[0]);
                this.loaderService.setItem('claimDetails', JSON.stringify(resp.listClaimDetails[0].claims[0]));
                const getClaimNumber = resp.listClaimDetails[0].claims[0].claimNumber.split(' ').join('');
                this.loaderService.hideLoader();
                this.router.navigate([`claims/claim-detail/${getClaimNumber}`]);
                this.clearData();
            },
            error: (error) => {
                this.errorMsg.messageText = error.error.errorMessage;
                this.loaderService.hideLoader();
            }
        });
    }

    fetchClaimantDetails() {
        const requestAccountKey = this.getCurrentAccountCacheKey();
        let userId = this.loaderService.getItem('userId');
        let payload = {
            claimInfo: {
                claimantName: this.claimForm.controls['searchField'].value,
                orgnztnId: this.loaderService.getItem('orgId')
            },
            userId: userId,
            claimDetailType: 'CLAIM_MENU_BASIC_SEARCH',
            claimType: 'HARTFORD',
            actionType: 'RETRIEVE'
        };
        this.claimSearchSubscription$ = this.claimsService.searchClaimDetailByClaimNumber(payload).subscribe({
            next: async (resp: any) => {
                if (this.getCurrentAccountCacheKey() !== requestAccountKey) {
                    return;
                }

                if (this.shouldUseClaimListValidation()) {
                    const filteredResp = this.filterClaimantResultsByValidatedClaims(resp);
                    if (!filteredResp) {
                        this.errorMsg.messageText = ClaimMenuComponent.NO_MATCH_ERROR;
                        return;
                    }
                    resp = filteredResp;
                }

                this.SharedService.setclaimantDetails(resp.listClaimDetails[0].claims[0]);
                payload['accessedFrom'] = 'account-home';
                payload['listType'] = 'NameList';
                payload['filterId'] = resp.claimAllFiltersCriteria?.claimFilterCriteria?.filterId;
                this.router.navigate([`claims/claim-lists`, payload]);
            },
            error: (error) => {
                this.errorMsg.messageText = error.error.message;
            }
        });
    }

    ngOnDestroy() {
        if (this.currentNumberSubscription$) this.currentNumberSubscription$.unsubscribe();
        if (this.claimSearchSubscription$) this.claimSearchSubscription$.unsubscribe();
    }
}
